import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { fetchLearnLibrary } from '@api/taptotip';
import { isPaymentsCompany, isTapToTipGuide } from '@utils/helpers';
import { learnModulePath } from './ctaTargets';

// Card 119 — the guide-facing sibling of AgentSummary.
//
// One card, one module: whichever piece of advice the guide's own account state
// says is most useful right now. When nothing is triggered the endpoint returns
// no module and this renders nothing — a permanent card is wallpaper, and
// wallpaper is ignored exactly when it finally matters.
//
// Guides only. Admins get AgentSummary in this slot, and coaching written for
// guides reads as noise on an operator's dashboard. The library itself is still
// open to admins from the left nav (see canSeeGuideAdvice).
const GuideAdviceCard = ({ user }) => {
  const [module, setModule] = useState(null);
  const history = useHistory();

  const isEligible = isTapToTipGuide(user) && !isPaymentsCompany(user?.companyId, user);

  useEffect(() => {
    if (!isEligible) return undefined;

    let cancelled = false;
    fetchLearnLibrary()
      .then(library => {
        if (cancelled || !library || !library.nextBestModuleId) return;
        const next = (library.modules || []).find(item => item.id === library.nextBestModuleId);
        if (next) setModule(next);
      })
      .catch(err => {
        // Advice is never the reason a dashboard fails to render.
        console.error('Failed to load guide advice:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isEligible]);

  if (!isEligible || !module) return null;

  return (
    <div
      className="w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0"
      data-testid="guide-advice-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold px-2 py-1 rounded-lg border border-solid uppercase tracking-wider bg-indigo-50 text-indigo-700 border-indigo-100">
          Worth reading
        </span>
        <span className="text-xs text-slate-400 font-semibold">{module.readMinutes} min read</span>
      </div>

      <h4 className="text-sm font-black text-slate-900 mt-3 mb-1">{module.title}</h4>
      <p className="text-sm text-slate-500 leading-relaxed m-0">{module.summary}</p>

      <button
        type="button"
        onClick={() => history.push(learnModulePath(module.id))}
        className="mt-4 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors"
      >
        Read this
      </button>
    </div>
  );
};

GuideAdviceCard.propTypes = {
  user: PropTypes.object
};

export default GuideAdviceCard;
